import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, UserCircle2 } from "lucide-react";

const LINKS = [
  { to: "/", label: "Dashboard", end: true },
  { to: "/statements", label: "Statements" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

export default function AuthorLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-hh-bg">
      <header
        className="sticky top-0 z-30 backdrop-blur-md bg-hh-bg/80 border-b border-hh-border"
        data-testid="author-header"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-12 py-5 flex items-center gap-6">
          <NavLink to="/" className="flex items-center gap-2.5 group" data-testid="brand-link">
            <img
              src="/healing-house-logo.png"
              alt="Healing House Publishing"
              className="h-11 w-11 object-contain shrink-0"
            />
            <div className="leading-none">
              <div className="font-serif text-lg text-hh-ink">Healing House</div>
              <div className="hh-overline text-[10px] mt-0.5">Author Portal</div>
            </div>
          </NavLink>
          <nav className="hidden md:flex items-center gap-1 ml-6" data-testid="author-nav">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`nav-${l.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-full text-sm transition-all ${
                    isActive ? "text-hh-ink bg-hh-muted" : "text-hh-ink2 hover:text-hh-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm text-hh-ink">{user?.name || "Author"}</span>
              <span className="text-xs text-hh-ink2">{user?.email}</span>
            </div>
            <NavLink
              to="/account"
              data-testid="account-link"
              className={({ isActive }) =>
                `p-2 rounded-full transition-all ${isActive ? "text-hh-ink bg-hh-muted" : "text-hh-ink2 hover:text-hh-ink hover:bg-hh-muted"}`
              }
              aria-label="Account settings"
              title="Account settings"
            >
              <UserCircle2 size={20} strokeWidth={1.5} />
            </NavLink>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="p-2 rounded-full text-hh-ink2 hover:text-hh-ink hover:bg-hh-muted transition-all"
              aria-label="Logout"
            >
              <LogOut size={18} strokeWidth={1.5} />
            </button>
          </div>
        </div>
        {/* Mobile nav */}
        <nav className="md:hidden flex items-center gap-1 overflow-x-auto px-6 pb-3 -mt-2" data-testid="author-nav-mobile">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-all ${
                  isActive ? "text-hh-ink bg-hh-muted" : "text-hh-ink2"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="max-w-6xl mx-auto px-6 md:px-10 lg:px-12 py-10 md:py-16">
        <Outlet />
      </main>
      <footer className="mt-24 py-10 border-t border-hh-border">
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-12 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-hh-ink2">
          <span>© {new Date().getFullYear()} Healing House Publishing</span>
          <span className="hh-overline">Made with quiet care</span>
        </div>
      </footer>
    </div>
  );
}
